import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DESK_NATURES, natureLabel, type DeskDetail, type DeskNature, type DeskPatchBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Select } from '@/components/ui/input';
import { errorMessage } from '@/features/desks/desks-queries';
import { useTenantProfile } from '@/shared/tenant-profile';

/**
 * Natureza do serviço da mesa (spec 017, FR-003). Até aqui o tipo da OS era
 * adivinhado pelo nome da mesa a cada OS; agora é esta escolha. Nasceu pela
 * sugestão do nome (migration, espelho, criação) e é revisável aqui — também
 * em mesa espelhada: o TiFlux não tem o conceito, não há o que sobrescrever.
 * `Interna` nunca aparece para o cliente (019).
 */
export function DeskNatureSection({ desk }: { desk: DeskDetail }) {
  const queryClient = useQueryClient();
  const { terms } = useTenantProfile();

  const save = useMutation({
    mutationFn: async (nature: DeskNature) => {
      const body: DeskPatchBody = { nature };
      const res = await api<DeskDetail>(`/desks/${desk.id}`, { method: 'PATCH', body });
      if (res.error) throw res.error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['erp', 'desks'] }),
  });

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">Natureza do serviço</h2>
        <Select
          aria-label="Natureza do serviço"
          className="h-8 w-auto min-w-44"
          value={desk.nature}
          disabled={save.isPending}
          onChange={(e) => save.mutate(e.target.value as DeskNature)}
        >
          {DESK_NATURES.map((nature) => (
            <option key={nature} value={nature}>
              {natureLabel(terms, nature)}
            </option>
          ))}
        </Select>
      </div>
      <p className="text-xs text-muted-foreground">
        Decide o tipo da ordem de serviço e como a mesa aparece nos indicadores.{' '}
        {desk.nature === 'internal'
          ? 'Interna: assunto da operação — nunca aparece para o cliente.'
          : null}
      </p>
      {save.error ? <p className="mt-2 text-sm text-destructive">{errorMessage(save.error)}</p> : null}
    </section>
  );
}
